import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { useProjects } from '../lib/store';
import { Project } from '../lib/types';
import { BlockRenderer } from '../components/renderer/BlockRenderer';
import { trackVisit } from '../lib/analytics';
export function ProjectDetailPage() {
  const {
    slug
  } = useParams();
  const {
    projects,
    isLoading
  } = useProjects();
  const [project, setProject] = useState<Project | null>(null);
  useEffect(() => {
    if (slug) trackVisit('/project/' + slug);
  }, [slug]);
  useEffect(() => {
    if (!isLoading) {
      // ISSUE 3 & 5 FIX: Find project by slug or id regardless of status
      // This allows previewing draft/unpublished content from the CMS
      const found = projects.find(p => p.slug === slug || p.id === slug);
      setProject(found || null);
    }
  }, [slug, projects, isLoading]);
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-sm" data-id="element-1701">
        Loading...
      </div>;
  }
  if (!project) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-sm" data-id="element-1702">
        Project not found
      </div>;
  }
  // ISSUE 5: The page builder blocks are rendered via BlockRenderer below.
  // The blocks array is populated by the BuilderTab in the admin CMS.
  const hasBlocks = project.blocks && project.blocks.length > 0;
  const containerStyle: React.CSSProperties = {
    ['--primary' as string]: project.accentColor || '72 100% 50%',
    backgroundColor: project.backgroundColor || 'hsl(var(--background))',
    color: project.textColor || 'hsl(var(--foreground))'
  };
  return <div className="min-h-screen bg-background text-foreground selection:bg-primary/30 selection:text-white pb-24" style={containerStyle} data-id="element-1703">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 mix-blend-difference p-6 md:p-12 pointer-events-none" data-id="element-1704">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors pointer-events-auto" data-id="element-1705">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1706" /> Back to Portfolio
        </Link>
      </nav>

      {/* Default Hero (if not overridden by a hero block) */}
      {project.heroStyle !== 'none' && !project.blocks.some(b => b.type === 'hero') && <header className="pt-40 pb-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-1707">
            <div className="font-display text-6xl md:text-8xl font-bold text-secondary leading-none mb-8 select-none" data-id="element-1708">
              {project.num}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-8" data-id="element-1709">
              {project.title}
            </h1>
            {project.subtitle && <p className="font-mono text-xl md:text-2xl text-muted-foreground max-w-3xl mb-16" data-id="element-1710">
                {project.subtitle}
              </p>}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-border pt-8" data-id="element-1711">
              <div data-id="element-1712">
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2" data-id="element-1713">
                  Role
                </div>
                <div className="font-mono text-sm" data-id="element-1714">{project.role}</div>
              </div>
              <div data-id="element-1715">
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2" data-id="element-1716">
                  Year
                </div>
                <div className="font-mono text-sm" data-id="element-1717">{project.year}</div>
              </div>
              <div data-id="element-1718">
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2" data-id="element-1719">
                  Category
                </div>
                <div className="font-mono text-sm" data-id="element-1720">{project.category}</div>
              </div>
              <div data-id="element-1721">
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2" data-id="element-1722">
                  Tools
                </div>
                <div className="font-mono text-sm" data-id="element-1723">
                  {project.tools.join(', ')}
                </div>
              </div>
            </div>
          </header>}

      {/* Cover Image */}
      {project.coverImage && !project.blocks.some(b => b.type === 'hero') && <div className="px-6 md:px-12 max-w-[1400px] mx-auto mb-24" data-id="element-1724">
          <img src={project.coverImage} alt={project.title} className="w-full aspect-video object-cover border border-border" data-id="element-1725" />
        </div>}

      {/* ISSUE 5: Render Page Builder Blocks */}
      {hasBlocks && <main data-id="element-1726">
          <BlockRenderer blocks={project.blocks} data-id="element-1727" />
        </main>}

      {/* Fallback content if no blocks exist */}
      {!hasBlocks && <main className="px-6 md:px-12 max-w-3xl mx-auto space-y-16" data-id="element-1728">
          {project.overview && <section data-id="element-1729">
              <h2 className="font-display text-3xl font-bold mb-4" data-id="element-1730">Overview</h2>
              <p className="font-mono text-sm text-muted-foreground leading-relaxed" data-id="element-1731">
                {project.overview}
              </p>
            </section>}
          {project.challenge && <section data-id="element-1732">
              <h2 className="font-display text-3xl font-bold mb-4" data-id="element-1733">
                The Challenge
              </h2>
              <p className="font-mono text-sm text-muted-foreground leading-relaxed" data-id="element-1734">
                {project.challenge}
              </p>
            </section>}
          {project.approach && <section data-id="element-1735">
              <h2 className="font-display text-3xl font-bold mb-4" data-id="element-1736">Approach</h2>
              <p className="font-mono text-sm text-muted-foreground leading-relaxed" data-id="element-1737">
                {project.approach}
              </p>
            </section>}
          {project.outcome && <section data-id="element-1738">
              <h2 className="font-display text-3xl font-bold mb-4" data-id="element-1739">Outcome</h2>
              <p className="font-mono text-sm text-muted-foreground leading-relaxed" data-id="element-1740">
                {project.outcome}
              </p>
            </section>}
        </main>}

      {/* Custom CSS Injection */}
      {project.customCSS && <style dangerouslySetInnerHTML={{
      __html: project.customCSS
    }} data-id="element-1741" />}
    </div>;
}